import { useEffect, useRef, useState } from 'react';
import { archiveUrl, signOutUrl } from './api';
import type { Me } from './api';
import { Mark } from './Mark';
import { hrefFor } from './routes';
import type { Route } from './routes';

/**
 * The frame every page except the call hangs in.
 *
 * WHY THIS EXISTS. There were seven hand-rolled `<header className="mx-topbar">`
 * blocks — one per page — and each carried a DIFFERENT set of links: the call
 * offered Home/Meetings/Status, the archive offered Search/Home, governance
 * offered Meetings/Home, search offered Home/Meetings. No page showed the whole
 * app, so the app's real site map was a row of five text buttons in the
 * dashboard's footer, and reaching governance from a search result meant going
 * home first. Adding a destination meant remembering six files.
 *
 * Now there is one nav, built from one list, rendered beside every page. Adding
 * a destination is a row in NAV and a case in `routes.ts` — which is the point,
 * because the list of places to be is about to grow (scheduling, rooms,
 * membership) and the previous arrangement did not have room for any of them.
 *
 * THE CALL IS NOT IN HERE, deliberately. A meeting wants the whole window, its
 * chrome reports things no other page has (who is here, how long, REC), and
 * navigating away from it disconnects — so a persistent rail beside it would be
 * four links whose every use is a departure. `Call.tsx` keeps its own top bar.
 */

interface NavItem {
  /** Where it goes. `undefined` for the rows that are links out of the app. */
  route?: Route;
  href?: string;
  label: string;
  icon: React.ReactNode;
  /** Shown only to an operator. */
  op?: boolean;
}

/**
 * Sixteen-pixel stroke icons, inline.
 *
 * Inline rather than a sprite because there are six of them and a sprite is a
 * second request plus a build step; stroke rather than fill because every one of
 * them has to read at 16px against a dark surface, and `currentColor` is what
 * lets the active row's colour carry into the glyph without a second rule.
 *
 * `site/_icons.svg` is the marketing site's set and is deliberately not reused —
 * it is not served from the app's origin.
 */
function Icon({ d, circle }: { d?: string; circle?: [number, number, number] }) {
  return (
    <svg className="mx-nav-icon" viewBox="0 0 24 24" aria-hidden="true">
      {circle && <circle cx={circle[0]} cy={circle[1]} r={circle[2]} />}
      {d && <path d={d} />}
    </svg>
  );
}

/**
 * The destinations, in the order somebody needs them.
 *
 * "Is it happening / what did I miss" first, because that is what the front door
 * answers and what most visits are for; the archive next; the things you consult
 * rather than visit last.
 *
 * NOTHING IS LISTED THAT DOES NOT EXIST. Scheduling and rooms are coming and
 * each will be one row here — but a nav item that lands on an empty page is the
 * same mistake as the captions button the site draws and the control bar does
 * not have (see docs/PRODUCT-SURFACE.md §3), so they arrive with their pages.
 */
const NAV: NavItem[] = [
  {
    route: { name: 'dashboard' },
    label: 'Home',
    icon: <Icon d="M4 11l8-7 8 7M6 10v9h12v-9" />,
  },
  {
    route: { name: 'meetings' },
    label: 'Meetings',
    icon: <Icon d="M4 5h16v15H4zM4 9h16M9 3v4M15 3v4" />,
  },
  {
    // Before Rooms: "when is it" is asked far more often than "who can read it".
    route: { name: 'schedule' },
    label: 'Schedule',
    icon: <Icon d="M4 5h16v15H4zM4 10h16M8 3v4M16 3v4M9 15h6" />,
  },
  {
    route: { name: 'rooms' },
    label: 'Rooms',
    icon: <Icon d="M4 20V6l8-3 8 3v14M9 20v-6h6v6M4 20h16" />,
  },
  {
    route: { name: 'search' },
    label: 'Search',
    icon: <Icon circle={[11, 11, 7]} d="M20 20l-3.5-3.5" />,
  },
  {
    route: { name: 'admin' },
    label: 'Governance',
    icon: <Icon d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />,
  },
  {
    route: { name: 'diagnostics' },
    label: 'Status',
    icon: <Icon d="M3 13h4l3-7 4 14 3-7h4" />,
  },
  {
    // The raw per-track audio and video — an operator's job, and the one row
    // here that leaves the app. It was in the dashboard's footer, which meant
    // it was reachable from exactly one page.
    //
    // LEAVING THE APP IS THE WHOLE RISK OF THIS ROW. The archive is served by
    // cmd/portal/recordings.go and shares no chrome with this shell — there is
    // no rail over there and no hamburger, so nothing renders a way back except
    // what that template draws itself. It now draws one at every level; see the
    // note on `render` there.
    href: archiveUrl(),
    label: 'Recordings',
    icon: <Icon d="M4 7h16v12H4zM4 7l2-3h12l2 3M9 13h6" />,
    op: true,
  },
];

/** The two initials a name reduces to, for the account button. */
function initials(me: Me): string {
  const from = me.name || me.email || '?';
  const parts = from.split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

/**
 * Who you are, and the way out.
 *
 * A `<details>` rather than a hand-rolled popover: it is what `ExportMenu`
 * already uses, and it comes with the keyboard and the Escape key rather than
 * needing them added. The outside-click close is the one thing `<details>` does
 * not give, so it is the only behaviour here written by hand.
 */
function AccountMenu({ me }: { me: Me }) {
  const box = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = box.current;
      if (el?.open && e.target instanceof Node && !el.contains(e.target)) el.open = false;
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <details className="mx-acct" ref={box}>
      <summary aria-label="Your account">
        <span className="mx-acct-initials" aria-hidden="true">
          {initials(me)}
        </span>
        <span className="mx-acct-name">{me.name || me.email}</span>
        <span className="mx-acct-caret" aria-hidden="true">
          ▴
        </span>
      </summary>
      <div className="mx-acct-pop">
        <p className="mx-acct-who">
          <strong>{me.name || me.email}</strong>
          {me.email && me.name && <span className="mx-muted">{me.email}</span>}
          {/* The role, where the role is decided rather than in the page title.
              It was in the dashboard's top bar only, which is where somebody
              debugging "why can I not place a hold" could not see it. */}
          <span className="mx-acct-roles">
            {me.op && <span className="mx-badge mx-badge-op">op</span>}
            {me.guest && <span className="mx-badge">guest</span>}
            {!me.op && !me.guest && <span className="mx-badge">staff</span>}
          </span>
        </p>
        <a className="mx-acct-item" href={signOutUrl()}>
          Sign out
        </a>
      </div>
    </details>
  );
}

/**
 * A page inside the frame.
 *
 * `title` and `actions` replace what each page's own top bar used to carry. The
 * nav links those bars also carried are gone from them — this renders those once.
 */
export function AppShell({
  me,
  route,
  navigate,
  title,
  actions,
  mainClass,
  children,
}: {
  /**
   * Absent for somebody holding a guest's signed summary link. There is no
   * account behind one and never will be, so there is nothing for an account
   * menu to show and nowhere for a nav row to go — see the note in App.tsx.
   */
  me?: Me | null;
  /** What to mark as the current section. */
  route: Route;
  navigate: (r: Route) => void;
  title: string;
  /** Page-specific controls, shown at the end of the page bar. */
  actions?: React.ReactNode;
  /** The class the old page put on its own `<main>` — `mx-dash`, `mx-search`, … */
  mainClass?: string;
  children?: React.ReactNode;
}) {
  const [drawer, setDrawer] = useState(false);

  // Navigating must close the drawer, or on a phone the destination renders
  // behind a rail that is still covering it.
  useEffect(() => setDrawer(false), [route.name]);

  // Escape closes it, because a full-width overlay with no visible dismiss is a
  // trap for anybody not using a mouse.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawer(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  // A GUEST GETS NO NAV. Every row above either refuses them at the portal or is
  // about an archive they cannot read, and a rail of six links that all 403 is
  // worse than a rail with none. They still get the frame, so the page they were
  // sent still looks like part of a product.
  const guest = !me || !!me.guest;
  const items = guest ? [] : NAV.filter((n) => !n.op || me!.op);

  // IS THERE A DRAWER AT ALL? Only if something in it can be acted on.
  //
  // Somebody on a summary link has no nav (see above) and no account menu (there
  // is no account), so below 900px the hamburger opened a panel containing a
  // mark and a paragraph and nothing to click — a control that looks like the
  // way to the rest of the app and is not. With no drawer the rail is not a
  // drawer either: it is hidden at that width and the mark moves into the page
  // bar, so the page still reads as this product rather than as a bare list.
  const hasDrawer = items.length > 0 || !!me;
  const open = hasDrawer && drawer;

  return (
    <div className={`mx-app${open ? ' drawer' : ''}${hasDrawer ? '' : ' nodrawer'}`}>
      <div className="mx-side">
        {/* Not a link for a guest. The only thing behind it is the dashboard,
            which would bounce them to a sign-in they cannot complete — so for
            them the mark is what it looks like: a mark. */}
        {guest ? (
          <span className="mx-side-brand">
            <Mark />
          </span>
        ) : (
          <a
            className="mx-side-brand"
            href={hrefFor({ name: 'dashboard' })}
            onClick={(e) => {
              // A real href so it can be middle-clicked and read by a screen
              // reader as a link, intercepted so an ordinary click does not
              // reload the app.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              navigate({ name: 'dashboard' });
            }}
          >
            <Mark />
          </a>
        )}

        {items.length > 0 && (
          <nav className="mx-nav" aria-label="Sections">
            {items.map((n) =>
              n.href ? (
                <a className="mx-nav-item" key={n.label} href={n.href}>
                  {n.icon}
                  {n.label}
                </a>
              ) : (
                <button
                  className={`mx-nav-item${n.route!.name === route.name ? ' on' : ''}`}
                  key={n.label}
                  aria-current={n.route!.name === route.name ? 'page' : undefined}
                  onClick={() => navigate(n.route!)}
                >
                  {n.icon}
                  {n.label}
                </button>
              ),
            )}
          </nav>
        )}

        {/* TWO DIFFERENT GUESTS, and one sentence cannot serve both. Somebody
            holding a summary link has no session at all and is reading one
            meeting's written record; somebody who redeemed an INVITE has a
            session, is here to be in a call, and has not been sent a record of
            anything. Telling the second they were "sent a link to a transcript"
            describes a thing that did not happen. */}
        {guest && (
          <p className="mx-side-guest">
            {me
              ? 'You joined on an invitation, so this is the meeting and nothing else — the archive and the settings belong to the people who work here.'
              : 'You were sent a link to one meeting’s written record. There is no account behind it, and nothing else here to sign in to.'}
          </p>
        )}

        <span className="mx-spacer" />
        {me && <AccountMenu me={me} />}
      </div>

      {/* Dismisses the drawer by tapping the page, the gesture everybody tries
          first. Hidden from assistive tech: Escape and the toggle are the
          announced ways out, and a clickable div is neither. */}
      <div className="mx-scrim" onClick={() => setDrawer(false)} aria-hidden="true" />

      <div className="mx-main">
        <header className="mx-pagebar">
          {hasDrawer ? (
            <button
              className="mx-burger"
              onClick={() => setDrawer((v) => !v)}
              aria-expanded={open}
              aria-label={open ? 'Hide sections' : 'Show sections'}
            >
              {/* THE GLYPH FOLLOWS THE STATE. The button does not move when the
                  drawer opens — it cannot, or the spot you just tapped would
                  stop being the spot that closes it — so the only thing that can
                  say "this is now a close" is the icon. Three bars that stayed
                  three bars over an open drawer is the other half of why this
                  read as intermittent. */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          ) : (
            /* Not hidden from assistive tech, and not duplicated either: the
               rail's copy is `display:none` at this width and the CSS hides this
               one above it, so exactly one is in the accessibility tree. */
            <span className="mx-pagebar-brand">
              <Mark />
            </span>
          )}
          {/* A span, not a heading. It names the section rather than the
              document — the same job the `.mx-room` span did in the bars this
              replaces — and pages below it (a meeting, a summary) own the `h1`.
              Promoting this to one would give every such page two. */}
          <span className="mx-pagetitle">{title}</span>
          <span className="mx-spacer" />
          {actions}
        </header>
        <main className={mainClass}>{children}</main>
      </div>
    </div>
  );
}
